import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { LayoutContext } from '../../contexts/layout.context';

import { createTrialDraft, getTrialAvailability, siteAsset } from '../../api/trials';

import './trials-v2.scss';

const LABELS = ['Мова', 'Формат', 'Досвід', 'Вік', 'Час', 'Підтвердження'];
const QUESTIONS = ['Оберіть мову', 'Оберіть формат заняття', 'Ви вже вчили цю мову?', 'Для кого заняття?', 'Найближчий вільний час', 'Залишився останній крок'];
const STEP_EXP = 2;
const STEP_CONFIRM = 5;

const kindsOf = (direction) => {
  const list = [];
  const groups = {};
  (direction?.formats || []).forEach((item) => {
    if (!item.group) {
      list.push({ key: item.branch, isGroup: false, format: item, title: item.title, short: item.short || item.title, desc: item.desc });
      return;
    }
    if (!groups[item.group]) {
      groups[item.group] = { key: item.group, isGroup: true, list: [], title: item.group_title || item.short || item.title, short: item.group_title || item.short || item.title, desc: item.group_desc || item.desc };
      list.push(groups[item.group]);
    }
    groups[item.group].list.push(item);
  });
  return list;
};
const PICK_MS = 280;

const dayGroups = (age, dayLabels) => {
  if (!age?.days) return [];

  const groups = [];
  if (age.days.today?.length) groups.push({ key: 'today', label: 'Сьогодні', date: dayLabels?.today || '', slots: age.days.today });
  if (age.days.tomorrow?.length) groups.push({ key: 'tomorrow', label: 'Завтра', date: dayLabels?.tomorrow || '', slots: age.days.tomorrow });
  (age.days.other || []).forEach((group, idx) => groups.push({ key: `o${idx}`, label: group.date, date: '', slots: group.slots }));

  return groups;
};

const ICONS = {
  clock: [['c', 12, 12, 9], ['p', 'M12 7v5.2l3.2 2']],
  globe: [['c', 12, 12, 9], ['p', 'M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18']],
  gift: [['p', 'M3.5 11h17v9.5h-17zM2.5 7.2h19V11h-19zM12 7.2v13.3'], ['p', 'M12 7.2S10.6 3.4 8.3 3.4a2.2 2.2 0 0 0 0 4.4zM12 7.2s1.4-3.8 3.7-3.8a2.2 2.2 0 0 1 0 4.4z']],
  arrow: [['p', 'M5 12h14M13 6l6 6-6 6']],
  back: [['p', 'M19 12H5M11 6l-6 6 6 6']],
  send: [['p', 'M21 3 3 10.5l7 2.5 2.5 7L21 3zM10 13l4-4']],
};

const NO_FILL = { fill: 'none' };

const Icon = ({ name }) => (
  <svg className="tv2-i" viewBox="0 0 24 24" aria-hidden="true">
    {(ICONS[name] || []).map((item, idx) => (item[0] === 'c'
      ? <circle key={idx} cx={item[1]} cy={item[2]} r={item[3]} style={NO_FILL} />
      : <path key={idx} d={item[1]} style={NO_FILL} />))}
  </svg>
);

Icon.propTypes = {
  name: PropTypes.string.isRequired,
};

const directionInstrumental = (name) => {
  const n = String(name || '').trim();
  if (!n) return 'мовою';
  return /а$/i.test(n) ? `${n.slice(0, -1).toLowerCase()}ою` : 'мовою';
};

const lessonInfo = (direction, kind) => [
  'Тривалість 50 хв',
  'Знайомство з викладачем',
  kind && !kind.isGroup ? 'Діагностика Вашого рівня мови' : `Знайомство з ${directionInstrumental(direction?.name)}`,
  'Підбір програми для Вас',
];

const slotFreeText = (n) => {
  if (!(n > 1)) return 'вільно';
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return `${n} місця`;
  return `${n} місць`;
};

const TrialsV2 = () => {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [direction, setDirection] = useState(null);
  const [kind, setKind] = useState(null);
  const [format, setFormat] = useState(null);
  const [age, setAge] = useState(null);
  const [slot, setSlot] = useState(null);
  const [dayKey, setDayKey] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [done, setDone] = useState(null);
  const [picking, setPicking] = useState(null);
  const [landed, setLanded] = useState(-1);
  const pickTimer = useRef(null);
  const { setLayout } = useContext(LayoutContext);

  const load = () => {
    setLoadError('');
    setData(null);
    getTrialAvailability()
      .then((response) => setData(response.data))
      .catch(() => setLoadError('Не вдалося завантажити вільні пробні. Спробуйте оновити сторінку.'));
  };

  useEffect(() => {
    document.title = 'Безкоштовні пробні | New Brain';
    setLayout({ page: 'Безкоштовні пробні' });
    load();

    return () => clearTimeout(pickTimer.current);
  }, [setLayout]);

  const groups = useMemo(() => dayGroups(age, data?.dayLabels), [age, data]);
  const activeGroup = groups.find((group) => group.key === dayKey) || groups[0] || null;

  const hasExpStep = !kind || kind.isGroup;
  const visibleSteps = hasExpStep ? [0, 1, STEP_EXP, 3, 4, STEP_CONFIRM] : [0, 1, 3, 4, STEP_CONFIRM];
  const step = !direction ? 0 : !kind ? 1 : !format ? STEP_EXP : !age ? 3 : (!slot && !age.no_slots) ? 4 : STEP_CONFIRM;
  const stepNo = visibleSteps.indexOf(step) + 1;

  const reset = (level) => {
    setSendError('');
    if (level <= 0) setDirection(null);
    if (level <= 1) {
      setKind(null);
      setFormat(null);
    }
    if (level <= STEP_EXP) setFormat(null);
    if (level <= 3) setAge(null);
    if (level <= 4) {
      setSlot(null);
      setDayKey('');
    }
  };

  const pick = (key, apply, chipIndex) => {
    if (picking) return;

    setPicking(key);
    clearTimeout(pickTimer.current);
    pickTimer.current = setTimeout(() => {
      apply();
      setPicking(null);
      setLanded(chipIndex);
    }, PICK_MS);
  };

  const pickDirection = (item) => pick(`d${item.id}`, () => {
    reset(1);
    setDirection(item);
  }, 0);

  const pickKind = (item) => pick(`k${item.key}`, () => {
    reset(1);
    setKind(item);
    if (!item.isGroup) setFormat(item.format);
  }, 1);

  const goBack = () => {
    setLanded(-1);

    if (step === STEP_CONFIRM && !slot) {
      reset(3);
      return;
    }

    const prev = visibleSteps[Math.max(0, visibleSteps.indexOf(step) - 1)];
    if (prev === STEP_EXP) {
      setFormat(null);
      setAge(null);
      setSlot(null);
      setDayKey('');
      setSendError('');
      return;
    }
    reset(prev);
  };

  const optionClass = (key, base) => {
    if (!picking) return base;

    return `${base} ${picking === key ? 'is-picked' : 'is-scatter'}`;
  };

  const submit = async () => {
    setSending(true);
    setSendError('');

    const telegramWindow = window.open('', '_blank');

    try {
      const response = await createTrialDraft({
        event_trial_id: slot?.id || null,
        direction_id: direction.id,
        branch: format.branch,
        age_group_id: age.id || null,
      });
      const url = response?.data?.telegram_url;
      setDone({ url });

      if (url) {
        if (telegramWindow) {
          telegramWindow.location.href = url;
        } else {
          window.location.href = url;
        }
      } else if (telegramWindow) {
        telegramWindow.close();
      }
    } catch (e) {
      if (telegramWindow) telegramWindow.close();
      setSendError(e?.response?.data?.message || 'Не вдалося записатися. Спробуйте ще раз.');
      if (e?.response?.status === 409) {
        setSlot(null);
        load();
      }
    } finally {
      setSending(false);
    }
  };

  const hero = (
    <div className="tv2-hero">
      <h1 className="tv2-h1">
        <span className="tv2-h1__lead">Безкоштовний</span>
        <span className="tv2-h1__sub">пробний урок</span>
      </h1>
      <ul className="tv2-feats">
        <li><Icon name="globe" /><span>Онлайн з викладачем</span></li>
        <li><Icon name="gift" /><span>Повністю безкоштовно</span></li>
        <li><Icon name="clock" /><span>Запис за хвилину</span></li>
      </ul>
    </div>
  );

  if (loadError) {
    return (
      <div className="tv2 container">
        {hero}
        <div className="tv2-empty">
          <p>{loadError}</p>
          <button type="button" className="tv2-cta" onClick={load}>Спробувати ще раз</button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="tv2 container">
        {hero}
        <div className="tv2-skeleton" aria-busy="true">
          <span /><span /><span /><span />
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="tv2 container">
        {hero}
        <div className="tv2-empty tv2-done">
          <span className="tv2-done__icon" aria-hidden="true">✓</span>
          <h2>Майже готово!</h2>
          <p>
            Ми відкрили Telegram-бот — натисніть там «Старт», і запис {slot ? 'підтвердиться автоматично' : 'потрапить до менеджера, він підбере зручний час'}.
          </p>
          {done.url && (
            <a className="tv2-cta" href={done.url} target="_blank" rel="noopener noreferrer">
              <Icon name="send" />
              Відкрити Telegram ще раз
            </a>
          )}
          <button type="button" className="tv2-back" onClick={() => { setDone(null); reset(0); }}>Записатися на інший пробний</button>
        </div>
      </div>
    );
  }

  const directions = data.directions || [];
  const kindShort = kind
    ? (/визнач/i.test(kind.short) ? 'Рівень' : kind.short.replace(/^Пробний\s+/i, '').replace(/^./, (c) => c.toUpperCase()))
    : '';
  const chips = [
    direction && { label: direction.name, icon: direction.image, emoji: '🗣', level: 0 },
    kind && { label: kindShort, emoji: '🎯', level: 1 },
    kind && kind.isGroup && format && { label: format.exp_title || format.short, emoji: '📚', level: STEP_EXP },
    age && { label: String(age.label).replace(/\s*рок(ів|и|у)\s*$/i, ''), emoji: '👤', level: 3 },
    slot && { label: `${activeGroup?.label || ''}, ${slot.t}`, emoji: '🕒', level: 4 },
  ].filter(Boolean);

  return (
    <div className="tv2 container">
      {hero}

      <div className="tv2-progress">
        <div className="tv2-progress__cap">
          <span>Крок {stepNo} з {visibleSteps.length}</span>
          <b key={step}>{LABELS[step]}</b>
        </div>
        <div className="tv2-progress__bars">
          {visibleSteps.map((item) => (
            <span key={item} className={`tv2-bar ${item < step ? 'is-done' : ''} ${item === step ? 'is-active' : ''}`.trim()} />
          ))}
        </div>
      </div>

      {chips.length > 0 && (
        <div className="tv2-chosen">
          {chips.map((chip, idx) => (
            <button
              type="button"
              key={`${chip.level}-${chip.label}`}
              className={`tv2-chip ${idx === landed ? 'is-land' : ''}`.trim()}
              title="Змінити"
              onClick={() => { setLanded(-1); if (chip.level === STEP_EXP) { setFormat(null); setAge(null); setSlot(null); setDayKey(''); } else { reset(chip.level); } }}
            >
              {chip.icon
                ? <img src={siteAsset(chip.icon)} alt="" className="tv2-chip__flag" />
                : <span className="tv2-chip__i" aria-hidden="true">{chip.emoji}</span>}
              <span>{chip.label}</span>
              <span className="tv2-chip__x" aria-hidden="true">✕</span>
              {idx === landed && <span className="tv2-chip__ring" aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}

      <section className="tv2-screen" key={step}>
        <h2 className="tv2-h2">{QUESTIONS[step]}</h2>

        {step === 0 && (
          <div className="tv2-langs">
            {directions.map((item, idx) => (
              <button
                type="button"
                key={item.id}
                className={optionClass(`d${item.id}`, 'tv2-lang tv2-opt')}
                style={{ '--i': idx }}
                onClick={() => pickDirection(item)}
              >
                <span className="tv2-lang__flag">
                  <img
                    src={siteAsset(item.image)}
                    alt=""
                    onError={(e) => { if (item.image_fallback) e.currentTarget.src = siteAsset(item.image_fallback); }}
                  />
                </span>
                <span className="tv2-lang__name">{item.name}</span>
                <span className="tv2-lang__go"><Icon name="arrow" /></span>
              </button>
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="tv2-formats">
            {kindsOf(direction).map((item, idx) => (
              <button
                type="button"
                key={item.key}
                className={optionClass(`k${item.key}`, 'tv2-fmt tv2-opt')}
                style={{ '--i': idx }}
                onClick={() => pickKind(item)}
              >
                <span className="tv2-fmt__num">{idx + 1}</span>
                <span className="tv2-fmt__t">{item.short}</span>
                {item.desc && <span className="tv2-fmt__d">{item.desc}</span>}
              </button>
            ))}
          </div>
        )}

        {step === STEP_EXP && (
          <div className="tv2-formats">
            {kind.list.map((item, idx) => (
              <button
                type="button"
                key={item.branch}
                className={optionClass(`f${item.branch}`, 'tv2-fmt tv2-opt')}
                style={{ '--i': idx }}
                onClick={() => pick(`f${item.branch}`, () => setFormat(item), STEP_EXP)}
              >
                <span className="tv2-fmt__num">{idx + 1}</span>
                <span className="tv2-fmt__t">{item.exp_title || item.short || item.title}</span>
                {item.exp_desc && <span className="tv2-fmt__d">{item.exp_desc}</span>}
              </button>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="tv2-ages">
            {format.ages.map((item, idx) => (
              <button
                type="button"
                key={item.id}
                className={optionClass(`a${item.id}`, 'tv2-age tv2-opt')}
                style={{ '--i': idx }}
                onClick={() => pick(`a${item.id}`, () => setAge(item), 3)}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}

        {step === 4 && (
          <>
            <div className="tv2-tabs" role="tablist">
              {groups.map((group, idx) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeGroup?.key === group.key}
                  key={group.key}
                  className={`tv2-tab tv2-opt ${activeGroup?.key === group.key ? 'is-on' : ''}`.trim()}
                  style={{ '--i': idx }}
                  onClick={() => setDayKey(group.key)}
                >
                  {group.label}
                  {group.date && <em>{group.date}</em>}
                </button>
              ))}
            </div>
            <div className="tv2-slots" key={activeGroup?.key}>
              {(activeGroup?.slots || []).map((item, idx) => (
                <button
                  type="button"
                  key={item.id}
                  className={optionClass(`s${item.id}`, 'tv2-slot tv2-opt')}
                  style={{ '--i': idx }}
                  onClick={() => pick(`s${item.id}`, () => { setDayKey(activeGroup.key); setSlot(item); }, 4)}
                >
                  <b>{item.t}</b>
                  {item.f > 0 && <span>{slotFreeText(item.f)}</span>}
                </button>
              ))}
            </div>
          </>
        )}

        {step === STEP_CONFIRM && (
          <div className="tv2-confirm">
            <div className="tv2-card">
              <h3>{age.no_slots && !slot ? '🗓 Ваша заявка' : '🗓 Ваше заняття'}</h3>
              <div className="tv2-sum">
                {slot ? (
                  <div className="is-hl"><span>Дата і час</span><span>{activeGroup?.label}{activeGroup?.date ? `, ${activeGroup.date}` : ''}, {slot.t}</span></div>
                ) : (
                  <div className="is-hl"><span>Дата і час</span><span>підбере менеджер</span></div>
                )}
                <div><span>Мова</span><span>{direction.name}</span></div>
                <div><span>Формат</span><span>{kind.short}{kind.isGroup && format ? ` · ${String(format.exp_title || format.short).toLowerCase()}` : ''}</span></div>
                <div><span>Вік</span><span>{age.label}</span></div>
                <div><span>Тривалість</span><span>50 хв</span></div>
                <div className="is-free"><span>Ціна</span><span>Безкоштовно</span></div>
              </div>
              <p className="tv2-kyiv">
                {age.no_slots && !slot ? 'Менеджер звʼяжеться з вами і запропонує зручний час.' : 'за київським часом'}
              </p>
            </div>

            <div className="tv2-card tv2-card--cta">
              <h3>Що буде на уроці</h3>
              <div className="tv2-lesson-info">
                {lessonInfo(direction, kind).map((item) => <span key={item}>✓ {item}</span>)}
              </div>
              {sendError && <p className="tv2-error" role="alert">{sendError}</p>}
              <button type="button" className="tv2-cta tv2-cta--pulse" disabled={sending} onClick={submit}>
                <Icon name="send" />
                {sending ? 'Записуємо…' : (age.no_slots && !slot ? 'Залишити заявку' : 'Підтвердити в Telegram')}
              </button>
            </div>
          </div>
        )}

        {step > 0 && (
          <button type="button" className="tv2-back" onClick={goBack}>
            <Icon name="back" />
            Назад
          </button>
        )}
      </section>
    </div>
  );
};

export default TrialsV2;
